import React, {Fragment, type ReactElement, useState} from 'react';

import cs from 'classnames';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {ShareDocumentModal} from 'app/general/modals/ShareDocumentModal';
import {DocumentMetadataStatusButton} from 'app/general/structures/DocumentMetadataStatusButton';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import type {BaseActionableDocumentType} from 'app/general/types';
import type {ShareableContentType} from 'app/user/types';
import type {ContentType} from 'sweet-types';
type TooltipProps = {
  ct: ContentType;
  isPrivate: boolean;
  children: ReactElement;
};
export const DocumentPrivateStatusTooltip = ({
  isPrivate,
  ct,
  children,
}: TooltipProps) => (
  <LazyTooltip
    placement="top"
    tooltip={
      isPrivate
        ? gt(
            'This %s is private and can be accessed only by teammates with proper permission',
            getContentType(ct).name
          )
        : gt(
            'This %s is public and can be accessed by anyone',
            getContentType(ct).name
          )
    }
  >
    {children}
  </LazyTooltip>
);
type ButtonProps = {
  object: BaseActionableDocumentType;
  ct: ShareableContentType;
  isPrivate: boolean;
  canEdit: boolean;
};
export const DocumentPrivateStatusButton = ({
  object,
  ct,
  isPrivate,
  canEdit,
}: ButtonProps) => {
  const [visible, setVisible] = useState(false);
  return (
    <Fragment>
      <ShareDocumentModal
        show={visible}
        object={object}
        contentType={ct}
        onClose={() => setVisible(false)}
        hideTeammateTab={true}
        hidePublicTab={false}
        initiallySelectedTab="show-public"
      />
      <DocumentPrivateStatusTooltip isPrivate={isPrivate} ct={ct}>
        <DocumentMetadataStatusButton
          disabled={!canEdit}
          active={!isPrivate}
          onClick={() => setVisible(true)}
        >
          <i className="fa fa-fw fa-globe-americas" />
        </DocumentMetadataStatusButton>
      </DocumentPrivateStatusTooltip>
    </Fragment>
  );
};
type IconProps = {
  ct: ContentType;
  isPrivate: boolean;
  className?: string;
};
export const DocumentPrivateStatusIcon = ({
  ct,
  className,
  isPrivate,
}: IconProps) =>
  !isPrivate ? (
    <DocumentPrivateStatusTooltip isPrivate={isPrivate} ct={ct}>
      <span className={cs(className)}>
        <i className="fa fa-globe-americas" />
      </span>
    </DocumentPrivateStatusTooltip>
  ) : null;
